import { MessageCircle, Files, Settings, LogOut, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { ThemeToggle } from './ThemeToggle';
import logoBioconnection from '../../assets/logo-BC.png';
import clsx from 'clsx';

export function Sidebar({ activeTab, setActiveTab, user, onLogout, isMobile = false }) {
  const [isOpen, setIsOpen] = useState(false);

  const navigation = [
    { id: 'chat', name: 'Chat', icon: MessageCircle },
    { id: 'files', name: 'Bestanden', icon: Files },
  ];

  if (user?.is_admin) {
    navigation.push({ id: 'admin', name: 'Admin', icon: Settings });
  }

  const toggleSidebar = () => setIsOpen(!isOpen);

  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  if (isMobile) {
    return (
      <>
        <button
          onClick={toggleSidebar}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white dark:bg-dark-800 shadow-lg border border-gray-200 dark:border-dark-700"
        >
          {isOpen ? (
            <X className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          )}
        </button>

        {isOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
          />
        )}

        <div className={clsx(
          "lg:hidden fixed left-0 top-0 h-full w-64 bg-white dark:bg-dark-900 border-r border-gray-200 dark:border-dark-700 z-40 transform transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <SidebarContent
            navigation={navigation}
            activeTab={activeTab}
            onNavClick={handleNavClick}
            user={user}
            onLogout={onLogout}
          />
        </div>
      </>
    );
  }

  return (
    <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white dark:bg-dark-900 border-r border-gray-200 dark:border-dark-700">
      <SidebarContent
        navigation={navigation}
        activeTab={activeTab}
        onNavClick={handleNavClick}
        user={user}
        onLogout={onLogout}
      />
    </div>
  );
}

function SidebarContent({ navigation, activeTab, onNavClick, user, onLogout }) {
  const handleLogout = async () => {
    await onLogout();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-center p-8 border-b border-gray-200 dark:border-dark-700 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-primary-900/20 dark:via-dark-900 dark:to-secondary-900/20">
        <div className="text-center">
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img 
              src={logoBioconnection} 
              alt="Bioconnection Logo" 
              className="w-36 h-auto object-contain"
            />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
            Luna
            <div className="text-lg text-primary-600 dark:text-primary-400 font-semibold">
              HR AI Assistant
            </div>
          </h2>
        </div>
      </div>

      <nav className="flex-1 px-4 py-6 space-y-2">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => onNavClick(item.id)}
              className={clsx(
                "w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors duration-200",
                isActive
                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-800"
              )}
            >
              <Icon className="h-5 w-5" />
              <span className="font-medium">{item.name}</span>
            </button>
          );
        })}
      </nav>

      <div className="border-t border-gray-200 dark:border-dark-700 p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-100 to-secondary-100 dark:from-primary-900/30 dark:to-secondary-900/30 flex items-center justify-center border-2 border-primary-200 dark:border-primary-700">
            <span className="text-sm font-bold text-primary-700 dark:text-primary-300">
              {user?.name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {user?.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {user?.email}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-left rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm font-medium">Uitloggen</span>
          </button>
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}
